// Table views (migration 045): what a saved view keeps of the page it's on. The page's filters and tab are its URL's
// search, without what's open (a record, the side pane): picking a view puts its filters in place and keeps what's
// open. Used by web/src/lib/table-views.ts and DataTable.

/**
 * A table's layout: its columns in order (by key), the ones hidden, the optional ones shown (a column hidden until
 * someone shows it: Shopify's damaged, safety stock…), and widths someone dragged (px).
 */
export type Layout = { order?: string[]; hidden?: string[]; shown?: string[]; widths?: Record<string, number> };
export type Sort = { key: string; desc?: boolean };

/** A dragged width the server keeps, in px (shared/mutations.ts `tableLayout`). */
export const WIDTH_MIN = 40;
export const WIDTH_MAX = 2000;

/**
 * A layout as it can be saved: a width outside WIDTH_MIN–WIDTH_MAX is left out (that column keeps its own), so a narrow
 * fixed column (a photo, a #) that a drag pinned at its drawn width never turns the whole save away (Mitch,
 * 2026-11-04: "Too small: expected number to be >=40" on resizing a PO's columns).
 */
export function savableLayout(layout: Layout): Layout {
  if (!layout.widths) return layout;
  const widths = Object.fromEntries(
    Object.entries(layout.widths)
      .filter(([, w]) => Number.isFinite(w) && w >= WIDTH_MIN && w <= WIDTH_MAX)
      .map(([k, w]) => [k, Math.round(w)]),
  );
  return { ...layout, widths };
}

/** Search params that say what's open, not how a list is filtered: never saved in a view, and kept when one is picked. */
const OPEN_PARAMS = new Set(["open", "pane", "line", "spec", "edit", "item", "bill", "run", "rt", "variant", "new", "po"]);

/** The page's filters and tab as a view keeps them: what's open left out, empty ones too, in a steady order. */
export function viewSearch(search: string): string {
  const params = [...new URLSearchParams(search)].filter(([k, v]) => !OPEN_PARAMS.has(k) && v !== "");
  params.sort(([a], [b]) => a.localeCompare(b));
  return new URLSearchParams(params).toString();
}

/** The URL's search with a view's filters in place of the page's, keeping what's open. */
export function withViewSearch(search: string, filters: string): string {
  const next = new URLSearchParams(filters);
  for (const [k, v] of new URLSearchParams(search)) if (OPEN_PARAMS.has(k)) next.set(k, v);
  const qs = next.toString();
  return qs ? `?${qs}` : "";
}
